import React from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Text, Button, Link, Icon
} from 'components/ui';
import PropTypes from 'prop-types';
import localeUtils from 'utils/LanguageLocale';
import RewardCardEN from 'components/Content/BeautyInsider/BeautyInsiderRewardsSummary/locales/BeautyInsiderRewardsSummary_en_US';
import RewardCardFR from 'components/Content/BeautyInsider/BeautyInsiderRewardsSummary/locales/BeautyInsiderRewardsSummary_fr_CA';
import {
    colors, space, radii, fontSizes, lineHeights, fontWeights
} from 'style/config';
import RougeExclusiveBadge from 'components/Badges/RougeExclusiveBadge';

/**
 * Renders a Beauty Insider reward summary card.
 *
 * @param {Object} props Component props.
 * @param {string} props.title Reward title.
 * @param {string} props.primaryText Main reward value text.
 * @param {string} [props.secondaryText] Supporting reward text.
 * @param {string|React.ReactNode} props.buttonText Primary CTA label.
 * @param {string} [props.linkText] Secondary link label.
 * @param {string} [props.linkHref] Secondary link destination URL.
 * @param {Function} [props.onButtonClick] Callback for button click.
 * @param {Function} [props.onLinkClick] Callback for link click.
 * @param {Function} [props.onRemoveClick] Callback for remove action. Required when applied is true.
 * @param {boolean} [props.showRougeBadge=false] Whether to show the ROUGE EXCLUSIVE badge.
 * @param {React.ReactNode} [props.leftContent] Custom left-side content.
 * @param {boolean} [props.applied=false] Whether the reward is already applied.
 * @param {boolean} [props.disabled=false] Whether the button is disabled.
 * @returns {React.ReactElement}
 */
const RewardCard = ({
    title,
    primaryText,
    secondaryText,
    buttonText,
    linkText,
    linkHref,
    onButtonClick,
    onLinkClick,
    onRemoveClick,
    showRougeBadge = false,
    leftContent,
    applied = false,
    disabled = false,
    testId
}) => {

    return (
        <Box
            css={styles.card}
            boxShadow='light'
            data-testid={testId}
        >
            {showRougeBadge && (
                <Box css={styles.badgeContainer}>
                    <RougeExclusiveBadge />
                </Box>
            )}
            <Box css={leftContent ? styles.contentFlex : styles.contentContainer}>
                {!!leftContent && leftContent}
                {!leftContent && (
                    <>
                        <Text css={styles.title}>{title}</Text>

                        <Text css={styles.primaryText}>{primaryText}</Text>

                        {secondaryText && <Text css={styles.secondaryText}>{secondaryText}</Text>}
                    </>
                )}
            </Box>
            <Box css={styles.actionsContainer}>
                <Button
                    variant='secondary'
                    size='sm'
                    onClick={applied ? undefined : onButtonClick}
                    disabled={disabled}
                    css={styles.button}
                >
                    {applied ? (
                        <Box css={styles.buttonContent}>
                            <Icon
                                name='checkmark'
                                size='1em'
                                css={styles.checkmarkIcon}
                            />
                            <Text css={styles.appliedText}>{getText('ctaApplied')}</Text>
                            <Link
                                is={'p'}
                                css={styles.removeText}
                                onClick={onRemoveClick}
                                children={getText('ctaRemove')}
                            />
                        </Box>
                    ) : (
                        buttonText
                    )}
                </Button>

                {linkText && (
                    <Link
                        href={linkHref}
                        color='blue'
                        fontSize='sm'
                        onClick={onLinkClick}
                        css={styles.link}
                    >
                        {linkText}
                    </Link>
                )}
            </Box>
        </Box>
    );
};

const getText = localeUtils.getLocalisedDataFromResource(RewardCardEN, RewardCardFR);

const styles = {
    card: {
        borderRadius: `${radii[2]}px`,
        padding: `${space[5]}px ${space[4]}px`,
        display: 'flex',
        flexDirection: 'row',
        height: '100%',
        backgroundColor: colors.white,
        position: 'relative',
        justifyContent: 'space-between',
        alignItems: 'center',
        alignSelf: 'stretch'
    },
    badgeContainer: {
        position: 'absolute',
        top: space[1]
    },
    title: {
        fontSize: `${fontSizes.sm}px`,
        fontWeight: fontWeights.bold,
        marginBottom: space[1]
    },
    primaryText: {
        fontSize: `${fontSizes.lg}px`,
        fontWeight: fontWeights.bold,
        marginBottom: space[1],
        lineHeight: lineHeights.tight
    },
    secondaryText: {
        fontSize: `${fontSizes.sm}px`,
        lineHeight: lineHeights.tight
    },
    actionsContainer: {
        width: '132px',
        flexShrink: 1,
        display: 'flex',
        flexDirection: 'column'
    },
    contentFlex: {
        flex: 1
    },
    contentContainer: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
    },
    button: {
        width: '100%',
        marginBottom: space[2]
    },
    link: {
        display: 'block',
        textAlign: 'center'
    },
    buttonContent: {
        position: 'relative',
        display: 'inline-block'
    },
    appliedText: {
        color: colors.black,
        fontSize: `${fontSizes.sm}px`, // 12px
        fontWeight: fontWeights.bold,
        marginBottom: space[1]
    },
    checkmarkIcon: {
        position: 'absolute',
        transform: `translateX(calc(-100% - ${space[1]}px))`
    },
    removeText: {
        fontSize: `${fontSizes.xs}px`,
        fontWeight: fontWeights.normal,
        display: 'block',
        color: colors.blue
    }
};

RewardCard.propTypes = {
    title: PropTypes.string,
    primaryText: PropTypes.string,
    secondaryText: PropTypes.string,
    buttonText: PropTypes.node,
    linkText: PropTypes.string,
    linkHref: PropTypes.string,
    onButtonClick: PropTypes.func,
    onLinkClick: PropTypes.func,
    onRemoveClick: PropTypes.func,
    showRougeBadge: PropTypes.bool,
    leftContent: PropTypes.node,
    applied: PropTypes.bool,
    disabled: PropTypes.bool,
    testId: PropTypes.string
};

export default wrapFunctionalComponent(RewardCard, 'RewardCard');
